import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { LoginForm } from './auth/LoginForm';
import { SignupForm } from './auth/SignupForm';
import { Homepage } from './Homepage';
import { Dashboard } from './Dashboard';
import { SlideEditor } from './SlideEditor';

interface User {
  id: number;
  name: string;
  email: string;
  slug?: string;
}

const AuthWrapper: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/v1/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        localStorage.removeItem('token');
      }
    } catch (err) {
      console.error('Auth check failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = (_token: string, userData: any) => {
    setUser(userData);
    navigate('/dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center crudo-gradient">
        <div className="text-lg text-white">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={
        user ? <Navigate to="/dashboard" /> : <Homepage onNavigateToLogin={() => navigate('/login')} />
      } />
      <Route path="/login" element={
        user ? <Navigate to="/dashboard" /> : (
          <div className="min-h-screen flex items-center justify-center crudo-gradient">
            <div className="max-w-md w-full space-y-8 p-4">
              <LoginForm onSuccess={handleAuthSuccess} />
              <p className="text-center text-sm text-gray-400">
                Don't have an account?{' '}
                <button
                  onClick={() => navigate('/signup')}
                  className="font-medium text-crudo-accent hover:text-blue-400"
                >
                  Sign up
                </button>
              </p>
            </div>
          </div>
        )
      } />
      <Route path="/signup" element={
        user ? <Navigate to="/dashboard" /> : (
          <div className="min-h-screen flex items-center justify-center crudo-gradient">
            <div className="max-w-md w-full space-y-8 p-4">
              <SignupForm onSuccess={handleAuthSuccess} />
              <p className="text-center text-sm text-gray-400">
                Already have an account?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="font-medium text-crudo-accent hover:text-blue-400"
                >
                  Login
                </button>
              </p>
            </div>
          </div>
        )
      } />
      <Route path="/dashboard" element={
        user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />
      } />
      <Route path="/:userSlugOrId/:deckId" element={<SlideEditor />} />
      <Route path="/:userSlugOrId/:deckId/:slideId" element={<SlideEditor />} />
      <Route path="/:userSlugOrId/:deckId/:slideId/edit" element={<SlideEditor />} />
    </Routes>
  );
};

export const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthWrapper />
    </BrowserRouter>
  );
};